import type { GetServerSidePropsContext } from 'next';
import getConfig from 'next/config';

import {
  ABNORMAL_LEGACY_SUBDOMAINS,
  ErrorSubdomain,
  REDIRECT_PATH_QUERY_KEY,
} from '../../constants';

import { ErrorPage, NotFoundError, RedirectError } from './errors';
import { getHost, withoutStartingSlash } from './logics';

type Subdomain = string;
type Path = string | undefined;

export function isRootDomainServerSidePropsContext(
  context: GetServerSidePropsContext
): boolean {
  return (
    getHost(context) ===
    getConfig().publicRuntimeConfig.RECRUITING_SITE_ROOT_DOMAIN
  );
}

export function getSubdomainFromServerSidePropsContext(
  context: GetServerSidePropsContext
): Subdomain {
  const host = getHost(context);
  if (!host) {
    // NOTE: 호스트가 없는 경우 NotFoundError로 리다이렉트 처리하면 무한 루프를 돕니다.
    throw new ErrorPage('host가 없습니다.', ErrorSubdomain.NotFoundSubdomain);
  }
  if (isRootDomainServerSidePropsContext(context)) {
    throw new NotFoundError('host가 없거나 root domain입니다.');
  }

  const hostParts = host.split('.');
  const subdomain = hostParts[0];
  if (!subdomain) {
    throw new NotFoundError('subdomain을 찾을 수 없습니다.');
  }

  const lowerCaseSubdomain = subdomain.toLowerCase();
  if (subdomain !== lowerCaseSubdomain) {
    throw new RedirectError(
      '소문자로만 구성된 subdomain으로 접근해주세요.',
      lowerCaseSubdomain
    );
  }

  return subdomain;
}

export function getUrlQueryParamFromServerSidePropsContext(
  context: GetServerSidePropsContext,
  name: string
): string {
  const parsedUrlQueryParam = context.params?.[name];
  const urlQueryParam = Array.isArray(parsedUrlQueryParam)
    ? parsedUrlQueryParam[0]
    : parsedUrlQueryParam;

  if (!urlQueryParam) {
    throw new NotFoundError('urlQueryParam을 찾을 수 없습니다.');
  }

  return urlQueryParam;
}

export function getRedirectInfoFromServerSidePropsContext(
  context: GetServerSidePropsContext
): [Subdomain, Path] {
  const query = context.query[REDIRECT_PATH_QUERY_KEY];
  const redirectPath = Array.isArray(query) ? query[0] : query;
  if (!redirectPath) {
    throw new NotFoundError('redirect 정보를 찾을 수 없습니다.');
  }

  return getRedirectInfo(redirectPath);
}

export function getRedirectInfo(redirectPath: string): [Subdomain, Path] {
  // 1. Query Path로 전달받은 경로를 디코딩합니다.
  const decodedPath = withoutStartingSlash(decodeURIComponent(redirectPath));
  // 2. 예외저긍로 처리해야하는 legacy subdomain인지 확인합니다.
  const legacySubdomain = ABNORMAL_LEGACY_SUBDOMAINS.find(legacy =>
    decodedPath.startsWith(legacy)
  );

  const [subdomain, ...paths] = legacySubdomain
    ? getSubdomainAndPathsWithLegacySubdomain(decodedPath, legacySubdomain)
    : getSubdomainAndPaths(decodedPath);

  const path = paths.filter(Boolean).join('/') || undefined;
  if (!subdomain) {
    throw new NotFoundError('subdomain을 찾을 수 없습니다.');
  }

  return [subdomain, path];
}

// NOTE: legacy subdomain은 이전에 사용하던 비 정상적인 subdomain입니다.
function getSubdomainAndPathsWithLegacySubdomain(
  decodedPath: string,
  legacySubdomain: string
): Array<string> {
  const paths = decodedPath.replace(legacySubdomain, '').split('/');
  return [legacySubdomain, ...paths];
}

function getSubdomainAndPaths(decodedPath: string): Array<string> {
  return decodedPath.split('/');
}
